import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { TEST_IDS } from "@/constants/testIds";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const { id, title, handle, category, fit, price, color } = product;
  const images = product.images?.length > 0 ? product.images : product.image ? [product.image] : [];
  const [imgIdx, setImgIdx] = useState(0);
  const [touchStart, setTouchStart] = useState(null);

  const currentImage = images[imgIdx] || null;
  const hasMultiple = images.length > 1;

  const cycleImage = useCallback((dir, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setImgIdx((prev) => {
      if (dir === "next") return (prev + 1) % images.length;
      return prev === 0 ? images.length - 1 : prev - 1;
    });
  }, [images.length]);

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      cycleImage(diff > 0 ? "next" : "prev");
    }
    setTouchStart(null);
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({ id, title, handle, price, image: currentImage, category, variantId: product.variantId || null });
  };

  return (
    <motion.div
      data-testid={TEST_IDS.PRODUCT.card(id)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: index * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link to={`/product/${handle}`} className="group block">
        {/* Image area */}
        <div
          className="product-image-wrapper relative rounded-2xl overflow-hidden mb-4"
          onTouchStart={hasMultiple ? handleTouchStart : undefined}
          onTouchEnd={hasMultiple ? handleTouchEnd : undefined}
        >
          <div className="aspect-[3/4] relative" style={{ backgroundColor: color || "#FDEAEB" }}>
            {currentImage && (
              <img
                src={currentImage}
                alt={title}
                className="product-image w-full h-full object-cover"
                loading="lazy"
              />
            )}

            {/* Arrow buttons — show on hover when multiple images */}
            {hasMultiple && (
              <>
                <button
                  onClick={(e) => cycleImage("prev", e)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-4 h-4 text-plum" />
                </button>
                <button
                  onClick={(e) => cycleImage("next", e)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-4 h-4 text-plum" />
                </button>
              </>
            )}
          </div>

          {/* Checked for you badge */}
          <div
            data-testid={TEST_IDS.PRODUCT.badge(id)}
            className="absolute top-3 right-3 flex items-center gap-1.5 bg-plum text-white text-xs font-semibold px-3 py-1.5 rounded-full"
          >
            <Check className="w-3 h-3" />
            <span>Checked for you</span>
          </div>

          {/* Dot indicators */}
          {hasMultiple && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid={`image-dots-${id}`}>
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImgIdx(i); }}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                    i === imgIdx ? "bg-white w-3" : "bg-white/50"
                  }`}
                  aria-label={`Image ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* Add to bag overlay */}
          <div className="absolute inset-x-0 bottom-0 p-4 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
            <button
              data-testid={TEST_IDS.PRODUCT.addToCart(id)}
              onClick={handleAddToCart}
              className="w-full bg-white/95 backdrop-blur text-plum font-semibold text-sm py-3 rounded-xl hover:bg-white transition-colors duration-200"
            >
              Add to bag
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="space-y-1.5 px-1">
          {category && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-plum-light">
              {category}
            </p>
          )}
          <h3
            data-testid={TEST_IDS.PRODUCT.name(id)}
            className="font-serif text-lg font-semibold text-plum leading-snug"
          >
            {title}
          </h3>
          <div className="flex items-center gap-3">
            {fit && (
              <Badge
                data-testid={TEST_IDS.PRODUCT.fit(id)}
                className="bg-amber/15 text-amber border-0 text-xs font-semibold rounded-full px-2.5"
              >
                {fit}
              </Badge>
            )}
            <span
              data-testid={TEST_IDS.PRODUCT.price(id)}
              className="text-sm font-semibold text-plum"
            >
              ${typeof price === "number" ? price.toLocaleString() : price}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
